import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Archive, BadgeCheck, Copy, FileSignature, Loader2, Plus, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ConsentTemplate, ConsentTemplateStatus, consentTemplatesService, renderConsentText } from '../../services/consentTemplatesService';
import { apiErrorMessage } from '../../services/ttsVoicesService';

const inputClass = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:bg-slate-50 disabled:text-slate-500';
const labelClass = 'block text-sm font-semibold text-navy mb-1';
const sectionLabel = 'text-[11px] font-bold text-primary tracking-widest uppercase';
const primaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';
const secondaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60';

const SAMPLE_DELETION_DATE = '30 April 2027';

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const BADGE: Record<ConsentTemplateStatus, string> = {
  approved: 'bg-green-50 text-green-600 border-green-200',
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  retired: 'bg-slate-50 text-slate-500 border-slate-200',
};

interface Form {
  attendeeText: string;
  sponsorText: string;
  dietaryText: string;
  processors: string;
}

const formOf = (t: ConsentTemplate): Form => ({
  attendeeText: t.attendeeText, sponsorText: t.sponsorText, dietaryText: t.dietaryText, processors: t.processorList.join('\n'),
});

const bodyOf = (f: Form) => ({
  attendeeText: f.attendeeText, sponsorText: f.sponsorText, dietaryText: f.dietaryText,
  processorList: f.processors.split('\n').map(p => p.trim()).filter(Boolean),
});

const FIELDS: Array<{ key: keyof Omit<Form, 'processors'>; label: string; hint: string; max: number; rows: number }> = [
  { key: 'attendeeText', label: 'Attendee consent', hint: 'Shown to every attendee above the transcription and AI processing tick-boxes.', max: 10_000, rows: 10 },
  { key: 'sponsorText', label: 'Sponsor terms', hint: 'Shown only to sponsors, who agree for their organisation.', max: 10_000, rows: 6 },
  { key: 'dietaryText', label: 'Dietary notice', hint: 'Shown before the dietary tick-box: who sees it and when it is deleted.', max: 5_000, rows: 4 },
];

export default function ConsentWordingAdmin() {
  const [templates, setTemplates] = useState<ConsentTemplate[] | null>(null);
  const [approvedVersion, setApprovedVersion] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async (select?: number) => {
    try {
      const r = await consentTemplatesService.list();
      setTemplates(r.templates);
      setApprovedVersion(r.approvedVersion);
      setError('');
      setSelected(prev => select ?? prev ?? r.approvedVersion ?? r.templates[0]?.version ?? null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load consent wording'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => templates?.find(t => t.version === selected) ?? null, [templates, selected]);
  useEffect(() => { setForm(current ? formOf(current) : null); }, [current]);

  const editable = current?.status === 'draft';
  const dirty = !!(current && form && JSON.stringify(bodyOf(form)) !== JSON.stringify(bodyOf(formOf(current))));

  async function run(key: string, fn: () => Promise<number | void>, done: string) {
    setBusy(key);
    try {
      const select = await fn();
      await load(select ?? undefined);
      toast.success(done);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  }

  const newDraft = (copyFrom?: number) => run('new', async () => (await consentTemplatesService.create(copyFrom ? { copyFrom } : {})).version,
    copyFrom ? `Copied v${copyFrom} into a new draft` : 'New draft created');
  const save = () => current && form && run('save', async () => { await consentTemplatesService.update(current.version, bodyOf(form)); }, 'Draft saved');
  const approve = () => {
    if (!current || !form) return;
    const replacing = approvedVersion ? ` It replaces v${approvedVersion}, which will be retired.` : '';
    if (!window.confirm(`Approve v${current.version}? Approved wording can't be edited.${replacing}`)) return;
    run('approve', async () => {
      if (dirty) await consentTemplatesService.update(current.version, bodyOf(form));
      await consentTemplatesService.approve(current.version);
    }, `v${current.version} approved`);
  };
  const retire = () => {
    if (!current) return;
    const msg = current.status === 'approved'
      ? `Retire v${current.version}? Until another version is approved, packs can't ask for consent.`
      : `Discard draft v${current.version}? It will be kept as retired.`;
    if (!window.confirm(msg)) return;
    run('retire', async () => { await consentTemplatesService.retire(current.version); }, `v${current.version} retired`);
  };

  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLTextAreaElement>) => form && setForm({ ...form, [key]: e.target.value });

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <FileSignature size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Consent Wording</h1>
              <p className="text-slate-500 text-sm font-medium">
                What Session Hub attendees agree to before a session. One version is approved at a time; approved wording never changes.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load()} title="Refresh" className={secondaryBtn}><RefreshCw size={16} /></button>
            <button onClick={() => newDraft()} disabled={busy === 'new'} className={primaryBtn}>
              {busy === 'new' ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} New draft
            </button>
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {templates && approvedVersion === null && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
            <AlertCircle size={16} className="shrink-0" /> No wording is approved. Session Hub packs can't ask for consent until a version is approved.
          </div>
        )}

        {!templates ? (
          !error && <div className="flex justify-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>
        ) : templates.length === 0 ? (
          <div className="rounded-xl bg-white p-12 border border-slate-200 text-center">
            <p className="text-slate-500 text-sm">No consent wording yet. Start with a new draft.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <p className={`${sectionLabel} px-5 pt-4 pb-2`}>Versions</p>
              <ul className="divide-y divide-[#e2e8f0]">
                {templates.map(t => (
                  <li key={t.version}>
                    <button
                      type="button" onClick={() => setSelected(t.version)}
                      className={`w-full text-left px-5 py-3 flex items-center justify-between gap-2 transition-colors ${t.version === selected ? 'bg-primary/5' : 'hover:bg-slate-50'}`}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-navy">Version {t.version}</span>
                        <span className="block text-xs text-slate-400">
                          {t.status === 'approved' ? `Approved ${fmtDate(t.approvedAt)}` : t.status === 'retired' ? `Retired ${fmtDate(t.retiredAt)}` : `Edited ${fmtDate(t.updatedAt)}`}
                        </span>
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${BADGE[t.status]}`}>{t.status}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {current && form && (
              <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-[#e2e8f0] flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-lg font-bold text-navy flex items-center gap-2">
                      Version {current.version}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${BADGE[current.status]}`}>{current.status}</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {current.copiedFrom ? `Copied from v${current.copiedFrom}. ` : ''}
                      {current.textSha256 ? <>Text hash <code className="text-slate-500">{current.textSha256.slice(0, 16)}…</code></> : 'Use {{deletionDate}} where the session\'s deletion date should appear.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editable && (
                      <button onClick={save} disabled={!dirty || !!busy} className={secondaryBtn}>
                        {busy === 'save' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save draft
                      </button>
                    )}
                    {editable && (
                      <button onClick={approve} disabled={!!busy} className={primaryBtn}>
                        {busy === 'approve' ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />} Approve
                      </button>
                    )}
                    {current.status !== 'draft' && (
                      <button onClick={() => newDraft(current.version)} disabled={!!busy} className={secondaryBtn}>
                        <Copy size={16} /> Copy to new draft
                      </button>
                    )}
                    {current.status !== 'retired' && (
                      <button onClick={retire} disabled={!!busy} className={secondaryBtn}>
                        {busy === 'retire' ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />} {editable ? 'Discard' : 'Retire'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2">
                  <div className="p-5 flex flex-col gap-5 xl:border-r border-[#e2e8f0]">
                    {FIELDS.map(f => (
                      <div key={f.key}>
                        <label htmlFor={`ct-${f.key}`} className={labelClass}>{f.label}</label>
                        <p className="text-xs text-slate-400 mb-2">{f.hint}</p>
                        <textarea
                          id={`ct-${f.key}`} value={form[f.key]} onChange={set(f.key)} disabled={!editable}
                          rows={f.rows} maxLength={f.max} className={`${inputClass} resize-y leading-relaxed`}
                        />
                      </div>
                    ))}
                    <div>
                      <label htmlFor="ct-processors" className={labelClass}>Processors</label>
                      <p className="text-xs text-slate-400 mb-2">One per line: who processes the data and for what. Up to 20.</p>
                      <textarea id="ct-processors" value={form.processors} onChange={set('processors')} disabled={!editable} rows={4} className={`${inputClass} resize-y`} />
                    </div>
                  </div>

                  <div className="p-5 bg-slate-50/50 flex flex-col gap-4">
                    <p className={sectionLabel}>Preview, with deletion date {SAMPLE_DELETION_DATE}</p>
                    {FIELDS.map(f => (
                      <div key={f.key} className="bg-white border border-[#e2e8f0] rounded-xl p-4">
                        <p className="text-xs font-semibold text-slate-400 mb-2">{f.label}</p>
                        <p className="text-sm text-navy whitespace-pre-wrap">
                          {form[f.key] ? renderConsentText(form[f.key], SAMPLE_DELETION_DATE) : <span className="text-slate-400">Not written yet</span>}
                        </p>
                      </div>
                    ))}
                    {bodyOf(form).processorList.length > 0 && (
                      <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
                        <p className="text-xs font-semibold text-slate-400 mb-2">Processors</p>
                        <ul className="list-disc pl-5 text-sm text-navy">
                          {bodyOf(form).processorList.map(p => <li key={p}>{p}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
